import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { RevealHeading, StaggerReveal } from "./Motion";
import { StaticWaveform, Waveform } from "./Waveform";
import { Stack, BnGlyph, Sparkle, DotGrid, Squiggle } from "./Decorations";
import { Play, BarChart3, Code2 } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

// Mini animated chart
const MiniChart = () => {
    const ref = useRef(null);
    useEffect(() => {
        if (!ref.current) return;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        const bars = ref.current.querySelectorAll("[data-bar]");
        gsap.from(bars, {
            scaleY: 0,
            transformOrigin: "bottom",
            duration: 0.9,
            stagger: 0.06,
            ease: "power3.out",
            scrollTrigger: { trigger: ref.current, start: "top 80%", once: true },
        });
    }, []);
    const heights = [38, 52, 44, 68, 56, 78, 64, 88, 92, 78, 96, 84];
    return (
        <svg ref={ref} viewBox="0 0 240 110" className="w-full h-32">
            <line x1="0" y1="100" x2="240" y2="100" stroke="#EFE8D8" strokeWidth="1"/>
            {heights.map((h, i) => (
                <rect
                    key={i}
                    data-bar
                    x={i * 20 + 4}
                    y={100 - h}
                    width="14"
                    height={h}
                    rx="3"
                    fill={i === heights.length - 1 ? "#FF5B3E" : "#0A0A0A"}
                />
            ))}
        </svg>
    );
};

export const Features = () => {
    return (
        <section id="features" className="relative py-24 lg:py-32 overflow-hidden">
            <Squiggle color="#7B3FE4" className="absolute top-20 left-[6%] w-28 animate-float-slow"/>
            <BnGlyph glyph="ফিচার" className="absolute -right-2 top-10 text-[140px] lg:text-[200px] leading-none font-bold opacity-[0.04] select-none" color="#0A0A0A"/>

            <div className="relative mx-auto max-w-[1240px] px-5 sm:px-8">
                <div className="max-w-[920px] mb-12 lg:mb-16">
                    <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-coral mb-5">Built different</p>
                    <RevealHeading as="h2" className="h-display text-[44px] sm:text-[64px] lg:text-[80px] text-ink">
                        Everything a Bengali newsroom actually needs.
                    </RevealHeading>
                </div>

                <StaggerReveal className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 auto-rows-[260px] gap-4 lg:gap-5">
                    {/* Embeddable Player - large */}
                    <article data-stagger data-testid="feature-player" className="lg:col-span-4 lg:row-span-2 bg-white rounded-3xl p-7 lg:p-9 border border-cream-300 shadow-soft hover:shadow-soft-lg transition-shadow relative overflow-hidden group">
                        <div className="flex items-center gap-2 mb-4">
                            <Play size={14} className="text-coral" fill="currentColor"/>
                            <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-muted">Embeddable Player</span>
                        </div>
                        <h3 className="font-display text-[36px] lg:text-[48px] font-semibold tracking-tight leading-[1.05] text-ink max-w-md">
                            One <span className="italic-display text-coral">line</span> of code. Plays everywhere.
                        </h3>
                        <p className="mt-4 text-[15px] text-ink-muted max-w-md">Drop it into your CMS, your WordPress site, your custom build. Auto-detects article body. Works in every browser since 2018.</p>
                        <div className="mt-7 bg-cream-100 rounded-2xl p-5 border border-cream-300">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-coral text-white flex items-center justify-center flex-shrink-0">
                                    <Play size={14} className="ml-0.5" fill="currentColor"/>
                                </div>
                                <div className="flex-1">
                                    <Waveform bars={40} color="#FF5B3E" height={40}/>
                                </div>
                                <span className="font-mono text-[11px] text-ink-muted">2:14</span>
                            </div>
                        </div>
                        <Sparkle color="#FFD23F" className="absolute top-5 right-5 w-7 h-7 group-hover:rotate-180 transition-transform duration-700"/>
                    </article>

                    {/* Smart Caching */}
                    <article data-stagger data-testid="feature-cache" className="lg:col-span-2 bg-mint/30 rounded-3xl p-6 border border-mint/40 shadow-soft hover:shadow-soft-lg transition-shadow relative overflow-hidden">
                        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-muted mb-3">Smart caching</div>
                        <h3 className="font-display text-[24px] lg:text-[28px] font-semibold tracking-tight text-ink leading-tight">
                            CDN-cached audio. Sub-200ms playback.
                        </h3>
                        <Stack color="#0A0A0A" className="absolute -bottom-2 -right-2 w-32 h-24 opacity-90"/>
                    </article>

                    {/* Pronunciation Engine */}
                    <article data-stagger data-testid="feature-pronunciation" className="lg:col-span-2 bg-coral text-cream-100 rounded-3xl p-6 shadow-soft hover:shadow-soft-lg transition-shadow relative overflow-hidden">
                        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-cream-100/70 mb-3">Pronunciation</div>
                        <h3 className="font-display text-[24px] lg:text-[28px] font-semibold tracking-tight leading-tight">
                            Trained on West Bengal & BD dialects.
                        </h3>
                        <p className="mt-3 text-[13px] text-cream-100/80">Place names, slang, brand names — handled.</p>
                        <BnGlyph glyph="অ" className="absolute -bottom-8 -right-2 text-[180px] leading-none font-bold opacity-30 select-none animate-float-slow" color="#FFD23F"/>
                    </article>

                    {/* Bangla UI Dashboard */}
                    <article data-stagger data-testid="feature-dashboard" className="lg:col-span-3 bg-white rounded-3xl p-6 border border-cream-300 shadow-soft hover:shadow-soft-lg transition-shadow relative overflow-hidden">
                        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-muted mb-3">Bangla dashboard</div>
                        <h3 className="font-display text-[24px] lg:text-[28px] font-semibold tracking-tight text-ink leading-tight mb-4">
                            <span className="bn">পুরো</span> dashboard in Bengali.
                        </h3>
                        <div className="bg-cream-100 rounded-2xl p-4 border border-cream-300">
                            <div className="flex items-center justify-between mb-3">
                                <span className="bn text-[12px] font-semibold text-ink">আজকের শোনা</span>
                                <span className="text-[11px] font-mono text-coral">+24%</span>
                            </div>
                            <div className="space-y-2">
                                {[
                                    { label: "অর্থনীতি", val: 78 },
                                    { label: "রাজনীতি", val: 56 },
                                    { label: "খেলাধুলা", val: 42 },
                                ].map((it, i) => (
                                    <div key={i} className="flex items-center gap-3">
                                        <span className="bn text-[11px] text-ink-muted w-16">{it.label}</span>
                                        <div className="flex-1 h-1.5 bg-cream-300 rounded-full overflow-hidden">
                                            <div className="h-full bg-coral rounded-full" style={{ width: `${it.val}%` }}/>
                                        </div>
                                        <span className="text-[10px] font-mono text-ink-muted">{it.val}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </article>

                    {/* WordPress + API */}
                    <article data-stagger data-testid="feature-api" className="lg:col-span-3 bg-ink-pill text-cream-100 rounded-3xl p-6 shadow-soft hover:shadow-soft-lg transition-shadow relative overflow-hidden">
                        <div className="flex items-center gap-2 mb-3">
                            <Code2 size={14} className="text-coral"/>
                            <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-cream-100/70">WordPress + API</span>
                        </div>
                        <h3 className="font-display text-[24px] lg:text-[28px] font-semibold tracking-tight leading-tight">
                            Plugin or REST. <span className="italic-display text-coral">Pick yours.</span>
                        </h3>
                        <div className="mt-4 font-mono text-[12px] text-cream-100/80 bg-black/30 rounded-xl p-3 border border-white/10">
                            <span className="text-mint">POST</span> /v1/articles<br/>
                            <span className="text-cream-100/40">{"{"}</span><br/>
                            &nbsp;&nbsp;<span className="text-sunshine">"text"</span>: <span className="text-coral bn">"আপনার লেখা..."</span><br/>
                            <span className="text-cream-100/40">{"}"}</span>
                        </div>
                    </article>

                    {/* Listener Analytics - large */}
                    <article data-stagger data-testid="feature-analytics" className="lg:col-span-6 bg-cream-200 rounded-3xl p-7 lg:p-9 border border-cream-300 shadow-soft hover:shadow-soft-lg transition-shadow relative overflow-hidden">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                            <div>
                                <div className="flex items-center gap-2 mb-4">
                                    <BarChart3 size={14} className="text-coral"/>
                                    <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-muted">Listener analytics</span>
                                </div>
                                <h3 className="font-display text-[32px] lg:text-[44px] font-semibold tracking-tight leading-[1.05] text-ink">
                                    Know exactly when readers <span className="italic-display text-coral">drop off.</span>
                                </h3>
                                <p className="mt-4 text-[15px] text-ink-muted max-w-md">Plays, completion rate, drop-off heatmaps, device breakdown, geo. Real-time. Exportable.</p>
                            </div>
                            <div className="bg-white rounded-2xl p-5 border border-cream-300 shadow-soft">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-muted">Plays this week</span>
                                    <span className="text-[12px] font-mono text-coral font-semibold">+38%</span>
                                </div>
                                <div className="font-display text-[40px] font-bold text-ink leading-none mb-3">142,089</div>
                                <MiniChart/>
                            </div>
                        </div>
                        <DotGrid color="#0A0A0A" rows={4} cols={4} className="absolute top-5 right-5 w-16 h-16 opacity-15"/>
                    </article>
                </StaggerReveal>
            </div>
        </section>
    );
};
